import { SearchIcon } from '@heroicons/react/outline';
import clsx, { ClassValue } from 'clsx';
import { FC, useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

interface ITableSearch {
  totalCount: number;
  search: string;
  onSearch: (search: string) => void;
  className?: ClassValue;
  theme?: 'default' | 'version-1';
}

const themes = {
  default: {
    searchWrapper: '',
    searchInner: 'flex-1',
    searchBox: 'w-full',
    input: 'rounded-md border border-gray-400/30 font-Sohne-Light'
  },
  'version-1': {
    searchWrapper: 'justify-end mr-5',
    searchInner: '',
    searchBox: 'w-56',
    input: 'border-0 bg-white font-Sohne-Light rounded-lg'
  }
};

const TableSearch: FC<ITableSearch> = ({
  totalCount,
  search,
  onSearch,
  className,
  theme = 'default'
}) => {
  const [value, setValue] = useState<string>(search);
  const [showSearch, setShowSearch] = useState<boolean>(false);

  const handleDebounceSearch = useDebouncedCallback((value: string) => {
    onSearch(value);
  }, 1000);

  useEffect(() => {
    if (theme === 'default') {
      setShowSearch(true);
    }
  }, [theme]);

  return (
    <div className={clsx('space-x-3 flex items-center', className, themes[theme].searchWrapper)}>
      {theme === 'default'}
      <div className={clsx('flex items-center gap-3', themes[theme].searchInner)}>
        {theme !== 'default' && (
          <button
            type="button"
            onClick={() => setShowSearch(!showSearch)}
            className="py-1.5 px-3 bg-white border border-gray-200 rounded-full hover:bg-gray-50 transition-colors cursor-pointer shrink-0"
          >
            <SearchIcon className="text-gray-600 h-4 w-4" />
          </button>
        )}

        {showSearch && (
          <div className={clsx('relative', themes[theme].searchBox)}>
            <input
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                handleDebounceSearch(e.target.value);
              }}
              placeholder={`Search ${totalCount} results..`}
              className={clsx(
                'text-xs p-2.5 w-full',
                'focus:outline-none focus:ring-0 focus:border-none',
                themes[theme].input
              )}
              autoFocus={showSearch}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default TableSearch;
